import type { TransitionKind } from '../state/editorStore';

export interface TransitionMeta {
  kind: TransitionKind;
  label: string;
  desc: string;
}

export const TRANSITIONS: TransitionMeta[] = [
  { kind: 'fade', label: 'Fade', desc: 'Dip to black and back' },
  { kind: 'flash', label: 'Flash', desc: 'Quick white flash' },
  { kind: 'zoomIn', label: 'Zoom in', desc: 'Punch in, then settle' },
  { kind: 'zoomOut', label: 'Zoom out', desc: 'Pull back, then settle' },
  { kind: 'slide', label: 'Slide', desc: 'Horizontal whip' },
];

export const TRANSITION_LABEL: Record<TransitionKind, string> = Object.fromEntries(
  TRANSITIONS.map((t) => [t.kind, t.label]),
) as Record<TransitionKind, string>;

/** Transition kinds that, at a cover B-roll boundary, become a REVEAL between
 *  the base video and the B-roll (the outgoing clip slides/fades/zooms out to
 *  expose the incoming one). Flash is excluded (it stays a masking pulse). */
export const BOUNDARY_TRANSITION_KINDS = ['slide', 'fade', 'zoomIn', 'zoomOut'];

export interface SlideLike { kind: string; startMs: number; durationMs: number }
/** For a cover B-roll, the boundary transitions on its start/end and the visible
 *  window widened to span them. Shared by the preview (TransitionOverlay +
 *  BrollLayer) and mirrored by the export compositor. */
export function brollBoundaryWindows(
  brollStartMs: number,
  brollEndMs: number,
  transitions: SlideLike[],
): { startTx?: SlideLike; endTx?: SlideLike; visStartMs: number; visEndMs: number } {
  const txs = transitions.filter((t) => BOUNDARY_TRANSITION_KINDS.includes(t.kind));
  const at = (ms: number) => txs.find((s) => ms >= s.startMs - 1 && ms <= s.startMs + s.durationMs + 1);
  const startTx = at(brollStartMs);
  const endTx = at(brollEndMs);
  return {
    startTx,
    endTx,
    // The clip stays visible across its boundary transitions (a start tx may
    // begin before the nominal start; an end tx may run past the nominal end).
    visStartMs: startTx ? Math.min(brollStartMs, startTx.startMs) : brollStartMs,
    visEndMs: endTx ? Math.max(brollEndMs, endTx.startMs + endTx.durationMs) : brollEndMs,
  };
}

/** Eased 0→1→0 pulse: a linear triangle run through smoothstep so the effect
 *  accelerates in and decelerates out instead of ramping mechanically. A raw
 *  linear ramp is the main reason transitions read as "off". */
function envelope(p: number): number {
  const t = p < 0.5 ? p * 2 : (1 - p) * 2; // linear triangle 0→1→0
  return t * t * (3 - 2 * t); // smoothstep ease-in-out
}

/** Eased monotonic 0→1 ramp (smoothstep) - for one-directional moves like the
 *  slide, which travels all the way across instead of springing back. */
export function ramp(p: number): number {
  const t = Math.min(1, Math.max(0, p));
  return t * t * (3 - 2 * t);
}

/** Visual effect for a transition at local progress p (0..1). Returns an
 *  overlay colour+opacity, a transform, and a filter for the footage layer, so
 *  preview and (later) export can share one definition. */
export function transitionEffect(kind: TransitionKind, p: number): {
  overlay: string; // rgba or ''
  transform: string; // css transform or ''
  filter: string; // css filter or ''
} {
  const e = envelope(p);
  switch (kind) {
    case 'fade':
      // Dip to black and back.
      return { overlay: `rgba(0,0,0,${e.toFixed(3)})`, transform: '', filter: '' };
    case 'flash':
      // White that builds and clears fast (pow<1 front-loads the peak).
      return { overlay: `rgba(255,255,255,${Math.pow(e, 0.6).toFixed(3)})`, transform: '', filter: '' };
    case 'zoomIn':
      // Punch in, then settle.
      return { overlay: '', transform: `scale(${(1 + 0.22 * e).toFixed(3)})`, filter: '' };
    case 'zoomOut':
      // Pull back to reveal the blurred backdrop, then settle.
      return { overlay: '', transform: `scale(${(1 - 0.2 * e).toFixed(3)})`, filter: '' };
    case 'slide':
      // Slide the whole scene fully off to the left (one direction, no spring
      // back); the layer behind - the held footage, or B-roll if present - is
      // revealed. No blur: the frame stays crisp as it travels.
      return {
        overlay: '',
        transform: `translateX(${(-100 * ramp(p)).toFixed(2)}%)`,
        filter: '',
      };
    default:
      return { overlay: '', transform: '', filter: '' };
  }
}
